import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import type { ContactList, ContactListMember, GhlContact } from './types';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import * as api from './actions';

const TagPills = memo(({ tags }: { tags: string[] | null }) => {
  if (!tags || tags.length === 0) return <span>—</span>;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {tags.map((tag) => (
        <span
          key={tag}
          style={{
            display: 'inline-block',
            padding: '0.1rem 0.4rem',
            borderRadius: '0.75rem',
            fontSize: '0.7rem',
            background: '#e8f0fe',
            color: '#1a56db',
            whiteSpace: 'nowrap',
          }}
        >
          {tag}
        </span>
      ))}
    </span>
  );
});
import classes from './contactLists.module.css';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  ssoToken: string;
  companyId: string;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

type View = 'list' | 'create' | 'detail' | 'edit';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ---------------------------------------------------------------------------
// Contact Picker
// ---------------------------------------------------------------------------

const PICKER_PAGE_SIZES = [20, 50, 100, 200, 500];
const DEFAULT_PICKER_PAGE_SIZE = 50;

const CONTACT_TYPES = ['lead', 'referral_partner'];

const buildDisplayName = (c: GhlContact): string => {
  if (c.display_name?.trim()) return c.display_name.trim();
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown';
};

interface ContactPickerProps {
  ssoToken: string;
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  excludeIds?: Set<string>;
}

function ContactPicker({ ssoToken, selected, onChange, excludeIds }: ContactPickerProps) {
  const [contacts, setContacts] = useState<GhlContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [dndFilter, setDndFilter] = useState<boolean | undefined>(undefined);
  const [perPage, setPerPage] = useState(DEFAULT_PICKER_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<{ current_page: number; last_page: number; total: number }>({ current_page: 1, last_page: 1, total: 0 });
  const [sortField, setSortField] = useState('display_name');
  const mountedRef = useRef(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!tagDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagDropdownOpen]);

  const toggleTagFilter = useCallback((tag: string) => {
    setTagFilter((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  }, []);

  const loadContacts = useCallback(async (p: number, searchTerm: string, type: string, tags: string[], dnd: boolean | undefined, sort: string, limit: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.searchContacts(ssoToken, {
        search: searchTerm || undefined,
        type: type || undefined,
        tags: tags.length ? tags : undefined,
        dnd,
        sort,
        page: p,
        limit,
      });
      if (!mountedRef.current) return;
      setContacts(result.contacts);
      setMeta(result.meta);
      setPage(p);
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load contacts');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [ssoToken]);

  // Load available tags once on mount
  useEffect(() => {
    api.fetchAvailableTags(ssoToken).then((tags) => {
      if (mountedRef.current) setAvailableTags(tags);
    }).catch(() => {});
  }, [ssoToken]);

  // Load on mount and when filters change
  useEffect(() => {
    loadContacts(1, committedSearch, typeFilter, tagFilter, dndFilter, sortField, perPage);
  }, [loadContacts, committedSearch, typeFilter, tagFilter, dndFilter, sortField, perPage]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCommittedSearch(value);
    }, 400);
  }, []);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setCommittedSearch(search);
    }
  }, [search]);

  const handleTypeChange = useCallback((value: string) => {
    setTypeFilter(value);
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortField((prev) => prev === field ? `-${field}` : prev === `-${field}` ? field : field);
  }, []);

  const handlePageChange = useCallback((p: number) => {
    loadContacts(p, committedSearch, typeFilter, tagFilter, dndFilter, sortField, perPage);
  }, [loadContacts, committedSearch, typeFilter, tagFilter, dndFilter, sortField, perPage]);

  const toggleContact = useCallback((id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  }, [selected, onChange]);

  const visibleContacts = useMemo(
    () => excludeIds?.size ? contacts.filter((c) => !excludeIds.has(c.id)) : contacts,
    [contacts, excludeIds],
  );

  const toggleAllOnPage = useCallback((checked: boolean) => {
    const next = new Set(selected);
    visibleContacts.forEach((c) => {
      if (checked) {
        next.add(c.id);
      } else {
        next.delete(c.id);
      }
    });
    onChange(next);
  }, [selected, onChange, visibleContacts]);

  const allOnPageSelected = visibleContacts.length > 0 && visibleContacts.every((c) => selected.has(c.id));

  const renderSortIndicator = (field: string) => {
    if (sortField === field) return ' \u2191';
    if (sortField === `-${field}`) return ' \u2193';
    return '';
  };

  return (
    <div className={classes.pickerContainer}>
      <div className={classes.pickerToolbar}>
        <input
          type="text"
          className={classes.pickerSearch}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search by name or email..."
        />
        <select
          className={classes.pickerTypeFilter}
          value={typeFilter}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <option value="">All types</option>
          {CONTACT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {availableTags.length > 0 && (
          <div ref={tagDropdownRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={classes.pickerTypeFilter}
              onClick={() => setTagDropdownOpen((o) => !o)}
              style={{ cursor: 'pointer', textAlign: 'left' }}
            >
              {/*
                MONGOOSE-BUG-095: the backend ORs selected tags (a contact matches if it carries
                ANY of them), so the multi-tag label must say "Any of" — a bare "3 tags" reads as
                all-of and misstates the result set.
              */}
              {tagFilter.length === 0 ? 'All tags' : tagFilter.length === 1 ? tagFilter[0] : `Any of ${tagFilter.length} tags`}
              {' ▾'}
            </button>
            {tagDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 100,
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '0.375rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '180px',
                maxHeight: '240px',
                overflowY: 'auto',
                padding: '0.25rem 0',
              }}>
                {tagFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setTagFilter([]); setTagDropdownOpen(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.35rem 0.75rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#6c757d' }}
                  >
                    Clear all
                  </button>
                )}
                {availableTags.map((t) => (
                  <label
                    key={t}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={tagFilter.includes(t)}
                      onChange={() => toggleTagFilter(t)}
                    />
                    {t}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        <select
          className={classes.pickerTypeFilter}
          value={dndFilter === undefined ? '' : dndFilter ? '1' : '0'}
          onChange={(e) => setDndFilter(e.target.value === '' ? undefined : e.target.value === '1')}
        >
          <option value="">All DND</option>
          <option value="1">DND on</option>
          <option value="0">DND off</option>
        </select>
        {selected.size > 0 && (
          <span className={classes.pickerSelectedCount}>
            {selected.size} selected
          </span>
        )}
        {loading && (
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
        )}
      </div>

      {error ? (
        <div className={classes.pickerEmpty}>
          <div className={classes.errorAlert}>{error}</div>
          <button type="button" className={classes.btnSecondary} onClick={() => loadContacts(page, committedSearch, typeFilter, tagFilter, dndFilter, sortField, perPage)}>Retry</button>
        </div>
      ) : visibleContacts.length === 0 && !loading ? (
        <div className={classes.pickerEmpty}>
          {committedSearch || typeFilter || tagFilter.length ? 'No contacts match your filters.' : 'No contacts found.'}
        </div>
      ) : (
        <>
          <div className={classes.pickerTableWrapper}>
            <table className={`table table-hover ${classes.table} ${classes.pickerTable}`}>
              <thead>
                <tr>
                  <th className={classes.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                    />
                  </th>
                  <th className={classes.sortableHeader} onClick={() => handleSort('display_name')}>
                    Name{renderSortIndicator('display_name')}
                  </th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className={classes.sortableHeader} onClick={() => handleSort('type')}>
                    Type{renderSortIndicator('type')}
                  </th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Source</th>
                  <th>Tags</th>
                  <th>DND</th>
                </tr>
              </thead>
              <tbody>
                {visibleContacts.map((c) => (
                  <tr
                    key={c.id}
                    className={selected.has(c.id) ? classes.selectedRow : ''}
                    onClick={() => toggleContact(c.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className={classes.checkboxCell} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleContact(c.id)}
                      />
                    </td>
                    <td>{buildDisplayName(c)}</td>
                    <td>{c.email || '\u2014'}</td>
                    <td>{c.phone || '\u2014'}</td>
                    <td>{c.type || '\u2014'}</td>
                    <td>{c.company_name || '\u2014'}</td>
                    <td>{[c.city, c.state].filter(Boolean).join(', ') || '\u2014'}</td>
                    <td>{c.source || '\u2014'}</td>
                    <td><TagPills tags={c.tags} /></td>
                    <td>{c.dnd ? '🚫' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={classes.pickerPagination}>
            <span className={classes.pageInfo}>
              {meta.total} contact{meta.total !== 1 ? 's' : ''}
              {meta.last_page > 1 && <>{' \u2014 '}Page {meta.current_page} of {meta.last_page}</>}
            </span>
            <div className={classes.pageButtons}>
              {meta.last_page > 1 && (
                <>
                  <button
                    type="button"
                    className={classes.pageButton}
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className={classes.pageButton}
                    disabled={page >= meta.last_page}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </button>
                </>
              )}
              <select
                className={classes.pickerTypeFilter}
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {PICKER_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List Detail (with members management)
// ---------------------------------------------------------------------------

interface ListDetailProps {
  list: ContactList;
  companyId: string;
  ssoToken: string;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onListUpdated: (list: ContactList) => void;
}

function ListDetail({ list, companyId, ssoToken, onBack, onEdit, onDelete, onListUpdated }: ListDetailProps) {
  const [members, setMembers] = useState<ContactListMember[]>([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberMeta, setMemberMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [addSelected, setAddSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load all member contact IDs for exclusion from picker
  const loadAllMemberIds = useCallback(async () => {
    const ids = new Set<string>();
    let page = 1;
    let lastPage = 1;
    try {
      do {
        const res = await api.listMembers(companyId, list.id, ssoToken, page);
        if (!mountedRef.current) return;
        res.data.forEach((m) => ids.add(m.contact_id));
        lastPage = res.meta.last_page;
        page += 1;
      } while (page <= lastPage);
      if (mountedRef.current) setMemberIds(ids);
    } catch {
      // best-effort
    }
  }, [companyId, list.id, ssoToken]);

  const loadMembers = useCallback(async (page: number) => {
    setLoadingMembers(true);
    setMemberError(null);
    setSelectedForRemoval(new Set());
    try {
      const res = await api.listMembers(companyId, list.id, ssoToken, page);
      if (!mountedRef.current) return;
      setMembers(res.data);
      setMemberMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setMemberPage(page);
    } catch {
      if (!mountedRef.current) return;
      setMemberError('Failed to load members');
    } finally {
      if (mountedRef.current) setLoadingMembers(false);
    }
  }, [companyId, list.id, ssoToken]);

  useEffect(() => {
    loadMembers(1);
    loadAllMemberIds();
  }, [loadMembers, loadAllMemberIds]);

  const refreshList = useCallback(async () => {
    try {
      const res = await api.getContactList(companyId, list.id, ssoToken);
      if (mountedRef.current) onListUpdated(res.data);
    } catch {
      // best-effort refresh
    }
  }, [companyId, list.id, ssoToken, onListUpdated]);

  const handleAddMembers = useCallback(async () => {
    if (addSelected.size === 0) return;
    setAdding(true);
    setActionError(null);
    try {
      await api.addMembers(companyId, list.id, Array.from(addSelected), ssoToken);
      if (!mountedRef.current) return;
      setAddSelected(new Set());
      await loadMembers(1);
      await loadAllMemberIds();
      await refreshList();
    } catch {
      if (!mountedRef.current) return;
      setActionError('Failed to add members');
    } finally {
      if (mountedRef.current) setAdding(false);
    }
  }, [companyId, list.id, ssoToken, addSelected, loadMembers, loadAllMemberIds, refreshList]);

  const toggleRemovalSelection = useCallback((contactId: string) => {
    setSelectedForRemoval((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  const handleRemoveSelected = useCallback(async () => {
    if (selectedForRemoval.size === 0) return;
    setRemoving(true);
    setActionError(null);
    try {
      await api.removeMembers(companyId, list.id, Array.from(selectedForRemoval), ssoToken);
      if (!mountedRef.current) return;
      setSelectedForRemoval(new Set());
      await loadMembers(1);
      await loadAllMemberIds();
      await refreshList();
    } catch {
      if (!mountedRef.current) return;
      setActionError('Failed to remove members');
    } finally {
      if (mountedRef.current) setRemoving(false);
    }
  }, [companyId, list.id, ssoToken, selectedForRemoval, loadMembers, loadAllMemberIds, refreshList]);

  return (
    <div>
      <div className={classes.detailHeader}>
        <div>
          <div style={{ marginBottom: '0.5rem' }}>
            <button type="button" className={classes.btnSecondary} onClick={onBack}>
              &larr; Back
            </button>
          </div>
          <h2 className={classes.detailTitle}>{list.name}</h2>
          {list.description && <p className={classes.detailDescription}>{list.description}</p>}
          <div className={classes.detailMeta}>
            <span className={classes.memberCount}>{list.members_count} member{list.members_count !== 1 ? 's' : ''}</span>
            <span className={classes.metaItem}>Created {formatDate(list.created_at)}</span>
          </div>
        </div>
        <div className={classes.btnGroup}>
          <button type="button" className={classes.btnSecondary} onClick={onEdit}>Edit</button>
          <button type="button" className={classes.btnDanger} onClick={onDelete}>Delete</button>
        </div>
      </div>

      {actionError && <div className={classes.errorAlert}>{actionError}</div>}

      {/* Add Members */}
      <div className={classes.addMembersSection}>
        <div className={classes.addMembersHeader}>
          <label className={classes.formLabel}>Add Contacts</label>
          <button
            type="button"
            className={classes.btnPrimary}
            disabled={adding || addSelected.size === 0}
            onClick={handleAddMembers}
          >
            {adding ? 'Adding...' : `Add ${addSelected.size > 0 ? `${addSelected.size} Contact${addSelected.size !== 1 ? 's' : ''}` : 'Contacts'}`}
          </button>
        </div>
        <ContactPicker
          ssoToken={ssoToken}
          selected={addSelected}
          onChange={setAddSelected}
          excludeIds={memberIds}
        />
      </div>

      {/* Members Table */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <h3 className={classes.cardTitle}>
            Members
            {memberMeta.total > 0 && <span className={classes.cardTitleCount}> ({memberMeta.total})</span>}
          </h3>
          {selectedForRemoval.size > 0 && (
            <button
              type="button"
              className={`${classes.btnDanger} ${classes.btnSmall}`}
              disabled={removing}
              onClick={handleRemoveSelected}
            >
              {removing ? 'Removing...' : `Remove ${selectedForRemoval.size} selected`}
            </button>
          )}
        </div>

        {loadingMembers ? (
          <div className={classes.loadingState}>
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <span>Loading members...</span>
          </div>
        ) : memberError ? (
          <div style={{ padding: '1rem' }}>
            <div className={classes.errorAlert}>{memberError}</div>
            <button type="button" className={classes.btnSecondary} onClick={() => loadMembers(memberPage)}>Retry</button>
          </div>
        ) : members.length === 0 ? (
          <div className={classes.emptyState}>
            <p className={classes.emptyTitle}>No members</p>
            <p className={classes.emptyText}>Add contacts using the field above.</p>
          </div>
        ) : (
          <>
            <div className={classes.tableWrapper}>
              <table className={`table table-hover ${classes.table}`}>
                <thead>
                  <tr>
                    <th className={classes.checkboxCell}>
                      <input
                        type="checkbox"
                        checked={members.length > 0 && members.every((m) => selectedForRemoval.has(m.contact_id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForRemoval(new Set(members.map((m) => m.contact_id)));
                          } else {
                            setSelectedForRemoval(new Set());
                          }
                        }}
                      />
                    </th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Type</th>
                    <th>Tags</th>
                    <th>DND</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className={selectedForRemoval.has(m.contact_id) ? classes.selectedRow : ''}>
                      <td className={classes.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={selectedForRemoval.has(m.contact_id)}
                          onChange={() => toggleRemovalSelection(m.contact_id)}
                        />
                      </td>
                      <td>{m.contact?.display_name || m.contact_id}</td>
                      <td>{m.contact?.email || '—'}</td>
                      <td>{m.contact?.phone || '—'}</td>
                      <td>{m.contact?.type || '—'}</td>
                      <td><TagPills tags={m.contact?.tags ?? null} /></td>
                      <td>{m.contact?.dnd ? '🚫' : '—'}</td>
                      <td>{formatDate(m.added_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {memberMeta.last_page > 1 && (
              <div className={classes.pagination}>
                <span className={classes.pageInfo}>Page {memberPage} of {memberMeta.last_page}</span>
                <div className={classes.pageButtons}>
                  <button
                    type="button"
                    className={classes.pageButton}
                    disabled={memberPage <= 1}
                    onClick={() => loadMembers(memberPage - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className={classes.pageButton}
                    disabled={memberPage >= memberMeta.last_page}
                    onClick={() => loadMembers(memberPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Container
// ---------------------------------------------------------------------------

function ContactListsContainer({ ssoToken, companyId }: Props) {
  const [view, setView] = useState<View>('list');
  const [lists, setLists] = useState<ContactList[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listMeta, setListMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [selectedList, setSelectedList] = useState<ContactList | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listToDelete, setListToDelete] = useState<ContactList | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSelectedContacts, setFormSelectedContacts] = useState<Set<string>>(new Set());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadLists = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listContactLists(companyId, ssoToken, page);
      if (!mountedRef.current) return;
      setLists(res.data);
      setListMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setListPage(page);
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load contact lists');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, ssoToken]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const loadListDetail = useCallback(async (listId: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getContactList(companyId, listId, ssoToken);
      if (!mountedRef.current) return;
      setSelectedList(res.data);
      setView('detail');
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load contact list');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, ssoToken]);

  // Navigation
  const goToList = useCallback(() => {
    setView('list');
    setSelectedList(null);
    setError(null);
    loadLists();
  }, [loadLists]);

  const goToCreate = useCallback(() => {
    setFormName('');
    setFormDescription('');
    setFormSelectedContacts(new Set());
    setFormErrors({});
    setView('create');
    setError(null);
  }, []);

  const goToEdit = useCallback(() => {
    if (!selectedList) return;
    setFormName(selectedList.name);
    setFormDescription(selectedList.description || '');
    setFormErrors({});
    setView('edit');
    setError(null);
  }, [selectedList]);

  // Create
  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!formName.trim()) errs.name = 'Name is required';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setError(null);
    const contactIds = Array.from(formSelectedContacts);
    try {
      const res = await api.createContactList(companyId, {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        contact_ids: contactIds.length > 0 ? contactIds : undefined,
      }, ssoToken);
      if (!mountedRef.current) return;
      setSelectedList(res.data);
      setView('detail');
      // Also refresh the list view data in the background
      loadLists();
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to create list');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [companyId, ssoToken, formName, formDescription, formSelectedContacts, loadLists]);

  // Update
  const handleUpdate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedList) return;
    const errs: Record<string, string> = {};
    if (!formName.trim()) errs.name = 'Name is required';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setError(null);
    try {
      const res = await api.updateContactList(companyId, selectedList.id, {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
      }, ssoToken);
      if (!mountedRef.current) return;
      setSelectedList(res.data);
      setView('detail');
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update list');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [companyId, ssoToken, selectedList, formName, formDescription]);

  // Delete
  const handleDelete = useCallback(async () => {
    if (!listToDelete) return;
    const toDelete = listToDelete;
    setListToDelete(null);
    setError(null);
    try {
      await api.deleteContactList(companyId, toDelete.id, ssoToken);
      if (!mountedRef.current) return;
      setView('list');
      setSelectedList(null);
      await loadLists();
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to delete list');
    }
  }, [listToDelete, companyId, ssoToken, loadLists]);

  const handleListUpdated = useCallback((updated: ContactList) => {
    setSelectedList(updated);
  }, []);

  return (
    <div className={classes.container}>
      {error && <div className={classes.errorAlert}>{error}</div>}

      {/* Confirm Delete */}
      {listToDelete && (
        <ConfirmDialog
          title="Delete Contact List"
          message={`Are you sure you want to delete "${listToDelete.name}"? This will remove all members from the list. This cannot be undone.`}
          confirmLabel="Delete"
          confirmClass={classes.btnDanger}
          onConfirm={handleDelete}
          onCancel={() => setListToDelete(null)}
        />
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>Contact Lists</h2>
              <p className={classes.subtitle}>Create and manage reusable contact lists for campaigns</p>
            </div>
            <button type="button" className={classes.btnPrimary} onClick={goToCreate}>
              + New List
            </button>
          </div>

          {loading ? (
            <div className={classes.loadingState}>
              <div className="spinner-border text-primary" role="status" />
              <span>Loading lists...</span>
            </div>
          ) : lists.length === 0 ? (
            <div className={classes.card}>
              <div className={classes.emptyState}>
                <p className={classes.emptyTitle}>No contact lists yet</p>
                <p className={classes.emptyText}>Create a list to organize contacts for your campaigns.</p>
                <button type="button" className={classes.btnPrimary} onClick={goToCreate}>
                  + New List
                </button>
              </div>
            </div>
          ) : (
            <div className={classes.card}>
              <div className={classes.tableWrapper}>
                <table className={`table table-hover ${classes.table}`}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Members</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lists.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <button
                            type="button"
                            className={classes.listName}
                            onClick={() => loadListDetail(l.id)}
                          >
                            {l.name}
                          </button>
                        </td>
                        <td><span className={classes.descriptionText}>{l.description || '—'}</span></td>
                        <td><span className={classes.memberCount}>{l.members_count}</span></td>
                        <td>{formatDate(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {listMeta.last_page > 1 && (
                <div className={classes.pagination}>
                  <span className={classes.pageInfo}>Page {listPage} of {listMeta.last_page}</span>
                  <div className={classes.pageButtons}>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={listPage <= 1}
                      onClick={() => loadLists(listPage - 1)}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={listPage >= listMeta.last_page}
                      onClick={() => loadLists(listPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* CREATE VIEW */}
      {view === 'create' && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>New Contact List</h2>
              <p className={classes.subtitle}>Create a reusable list of contacts</p>
            </div>
          </div>
          <form onSubmit={handleCreate} className={classes.formCard}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>List Name</label>
              <input
                type="text"
                className={classes.formInput}
                value={formName}
                onChange={(e) => { setFormName(e.target.value); setFormErrors((p) => { const n = { ...p }; delete n.name; return n; }); }}
                placeholder="e.g. VIP Customers, Past Clients"
              />
              {formErrors.name && <div className={classes.formError}>{formErrors.name}</div>}
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Description (optional)</label>
              <input
                type="text"
                className={classes.formInput}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="A brief description of this list"
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>
                Initial Contacts (optional)
                {formSelectedContacts.size > 0 && (
                  <span className={classes.pickerSelectedBadge}>{formSelectedContacts.size} selected</span>
                )}
              </label>
              <ContactPicker
                ssoToken={ssoToken}
                selected={formSelectedContacts}
                onChange={setFormSelectedContacts}
              />
              <div className={classes.formHint}>Optionally pre-populate the list with contacts. You can also add members later.</div>
            </div>
            <div className={classes.formActions}>
              <button type="button" className={classes.btnSecondary} onClick={goToList}>Cancel</button>
              <button type="submit" className={classes.btnPrimary} disabled={saving}>
                {saving ? 'Creating...' : 'Create List'}
              </button>
            </div>
          </form>
        </>
      )}

      {/* EDIT VIEW */}
      {view === 'edit' && selectedList && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>Edit List</h2>
              <p className={classes.subtitle}>{selectedList.name}</p>
            </div>
          </div>
          <form onSubmit={handleUpdate} className={classes.formCard}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>List Name</label>
              <input
                type="text"
                className={classes.formInput}
                value={formName}
                onChange={(e) => { setFormName(e.target.value); setFormErrors((p) => { const n = { ...p }; delete n.name; return n; }); }}
              />
              {formErrors.name && <div className={classes.formError}>{formErrors.name}</div>}
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Description (optional)</label>
              <input
                type="text"
                className={classes.formInput}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>
            <div className={classes.formActions}>
              <button type="button" className={classes.btnSecondary} onClick={() => setView('detail')}>Cancel</button>
              <button type="submit" className={classes.btnPrimary} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </>
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && selectedList && !loading && (
        <ListDetail
          list={selectedList}
          companyId={companyId}
          ssoToken={ssoToken}
          onBack={goToList}
          onEdit={goToEdit}
          onDelete={() => setListToDelete(selectedList)}
          onListUpdated={handleListUpdated}
        />
      )}

      {/* Detail loading */}
      {view === 'detail' && loading && (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status" />
          <span>Loading list...</span>
        </div>
      )}
    </div>
  );
}

const ContactListsContainerMemo = memo(ContactListsContainer, areEqual);

export { ContactListsContainerMemo as ContactListsContainer };
